import * as React from 'react';
import * as LucideIcons from 'lucide-react';
import { Achievement, tierColors } from '@/types/achievements';
import { cn } from '@/lib/utils';

interface AchievementBadgeProps {
  achievement: Achievement;
  unlocked?: boolean;
  size?: 'sm' | 'md' | 'lg';
  progress?: number;
  className?: string;
}

const sizeClasses = {
  sm: 'w-12 h-12',
  md: 'w-16 h-16',
  lg: 'w-24 h-24',
};

const iconSizes = {
  sm: 20,
  md: 28,
  lg: 40,
};

export const AchievementBadge: React.FC<AchievementBadgeProps> = ({
  achievement,
  unlocked = false,
  size = 'md',
  progress,
  className,
}) => {
  const IconComponent = (LucideIcons as any)[achievement.icon] || LucideIcons.Award;
  const tierColor = tierColors[achievement.tier];

  return (
    <div className={cn('relative', className)}>
      <div
        className={cn(
          sizeClasses[size],
          'rounded-full flex items-center justify-center relative',
          'border-2 transition-all duration-300',
          unlocked
            ? 'bg-gradient-to-br from-background to-muted shadow-lg'
            : 'bg-muted/50 grayscale opacity-60'
        )}
        style={{
          borderColor: unlocked ? tierColor : 'hsl(var(--muted))',
        }}
      >
        <IconComponent
          size={iconSizes[size]}
          className={cn(
            'transition-all',
            unlocked ? 'text-foreground' : 'text-muted-foreground'
          )}
        />
        
        {!unlocked && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/60 rounded-full">
            <LucideIcons.Lock size={iconSizes[size] * 0.5} className="text-muted-foreground" />
          </div>
        )}

        {progress !== undefined && progress < 100 && (
          <svg className="absolute inset-0 w-full h-full -rotate-90">
            <circle
              cx="50%"
              cy="50%"
              r="45%"
              stroke="hsl(var(--muted))"
              strokeWidth="2"
              fill="none"
            />
            <circle
              cx="50%"
              cy="50%"
              r="45%"
              stroke={tierColor}
              strokeWidth="2"
              fill="none"
              strokeDasharray={`${progress * 2.83} 283`}
              className="transition-all duration-500"
            />
          </svg>
        )}
      </div>
    </div>
  );
};
